import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, MessageSquare, ArrowRight } from "lucide-react";

/**
 * About — Phase 50.1 minimal premium rewrite.
 *
 * Per user spec ("Keep it short"): single hero with the brand statement,
 * three quiet credibility lines, and a single CTA back to the AI Concierge
 * on `/`. Matches the calm, luxurious tone of the new homepage.
 */
export default function About() {
  return (
    <div className="bg-white" data-testid="about-page">
      <section className="relative isolate overflow-hidden">
        {/* Soft ivory wash so this page feels editorial, not flat-white. */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#FFF8EE] via-white to-white" />

        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-24 sm:py-32 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#C9A45C]/35 px-3.5 py-1.5 text-[11px] tracking-[0.22em] uppercase text-[#064E3B] font-semibold shadow-[0_8px_22px_-14px_rgba(7,26,45,0.25)]">
            <Sparkles size={12} className="text-[#C9A45C]" />
            About TREL
          </div>

          <h1
            data-testid="about-headline"
            className="mt-8 font-serif text-[#071A2D] text-4xl sm:text-5xl lg:text-[60px] leading-[1.05]"
          >
            One request.
            <br />
            <span className="italic text-[#064E3B]">One concierge. One booking.</span>
          </h1>

          <p
            data-testid="about-statement"
            className="mt-8 text-[17px] sm:text-[19px] text-[#071A2D]/80 leading-[1.7] font-light max-w-2xl mx-auto"
          >
            TREL is an AI-powered concierge marketplace for travel,
            reservations, experiences, and lifestyle needs. Tell TREL
            what you want. Verified concierges handle the details.
          </p>

          {/* Three quiet credibility lines — minimal, icon-led */}
          <ul className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-left">
            {[
              { icon: MessageSquare, title: "Say it once",      body: "Describe what you want in plain words." },
              { icon: Sparkles,      title: "AI shapes it",     body: "TREL turns your idea into a clear brief in seconds." },
              { icon: ShieldCheck,   title: "Verified humans",  body: "Trusted concierges handle every detail end-to-end." },
            ].map(({ icon: Icon, title, body }) => (
              <li
                key={title}
                className="flex flex-col items-start gap-2"
                data-testid={`about-pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={18} className="text-[#C9A45C]" strokeWidth={1.8} />
                <div className="text-[#071A2D] text-[15px] font-medium">{title}</div>
                <p className="text-[13.5px] text-[#071A2D]/65 leading-relaxed">{body}</p>
              </li>
            ))}
          </ul>

          <div className="mt-14">
            <Link
              to="/"
              data-testid="about-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#064E3B] text-white hover:brightness-110 px-6 py-3 text-[14px] font-medium shadow-[0_12px_28px_-12px_rgba(6,78,59,0.7)] transition"
            >
              Tell TREL what you want
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
